// Niveau d'effort de l'assistant (sprint 6, LLM-10 réécrit par l'errata E022/E079).
// Fonctions pures, sans React ni accès direct à window : stockage injecté (tests node).
//
// Contrat (plan sprint 6, B2) : la requête de chat accepte « effort » :
//   "auto" | "rapide" | "standard" | "approfondi" ; absent = auto (le champ est omis).
// Le serveur renvoie le niveau appliqué dans `start` et `done` :
//   effort: {niveau, source: "auto"|"utilisateur", effort, reflexion, raison?, degrade?}
// (degrade: true → modèle sans niveau d'effort : niveau null, « profondeur fixe »)
// et /api/models ajoute effort:boolean et reflexion:boolean par modèle.

export const EFFORT_LEVELS = ['auto', 'rapide', 'standard', 'approfondi'];
export const DEFAULT_EFFORT = 'auto';

// Libellés de l'interface (court : bouton compact sur mobile)
export const EFFORT_OPTIONS = [
  { id: 'auto', label: 'Automatique', short: 'Auto', hint: "L'assistant choisit selon la question" },
  { id: 'rapide', label: 'Rapide', short: 'Rapide', hint: 'Réponses factuelles courtes' },
  { id: 'standard', label: 'Standard', short: 'Standard', hint: 'Analyse courante' },
  { id: 'approfondi', label: 'Approfondi', short: 'Approf.', hint: 'Arbitrages, rapports et plans d’action — plus lent' },
];

export const EFFORT_UNSUPPORTED_HINT = "Ce modèle n'ajuste pas sa profondeur de réflexion";

/** Valeur valide, sinon « auto » (préférence ancienne, modifiée à la main, illisible…). */
export function normalizeEffort(value) {
  return EFFORT_LEVELS.includes(value) ? value : DEFAULT_EFFORT;
}

/** Champ `effort` du corps de la requête : omis (undefined) pour « auto » ou une valeur invalide. */
export function effortForRequest(value) {
  return EFFORT_LEVELS.includes(value) && value !== DEFAULT_EFFORT ? value : undefined;
}

/** Le modèle ajuste-t-il sa profondeur ? Champ absent (serveur antérieur au sprint 6) : oui. */
export function modelSupportsEffort(model) {
  return model?.effort !== false;
}

const idPart = (value) => {
  if (value === null || value === undefined || value === '') return 'anonyme';
  return String(value).replace(/[^\w-]/g, '_');
};

/**
 * Clé de la préférence : digitrans_effort:<entreprise_id>:<user_id>.
 * Jamais le hash d'API ni l'adresse e-mail (lisibles dans le stockage du navigateur).
 */
export function effortPrefKey(context) {
  return `digitrans_effort:${idPart(context?.entreprise_id)}:${idPart(context?.user_id)}`;
}

/**
 * Lecture protégée. `memory` (Map) sert de repli quand le stockage est bloqué
 * (iframe tierce, navigation privée) : la préférence vaut alors pour la session.
 */
export function readEffortPref(storage, key, memory = null) {
  try {
    const raw = storage?.getItem(key);
    if (raw !== null && raw !== undefined) return normalizeEffort(JSON.parse(raw));
  } catch {
    /* stockage bloqué ou valeur illisible : repli en mémoire */
  }
  return normalizeEffort(memory?.get(key));
}

/** Écriture protégée ; la valeur est toujours gardée en mémoire (repli). Renvoie la valeur retenue. */
export function writeEffortPref(storage, key, value, memory = null) {
  const level = normalizeEffort(value);
  memory?.set(key, level);
  try {
    if (level === DEFAULT_EFFORT) storage?.removeItem(key);
    else storage?.setItem(key, JSON.stringify(level));
  } catch {
    /* stockage indisponible : le choix vaut pour la session en cours */
  }
  return level;
}

// ── Niveau appliqué (événements `start` / `done`) ───────────────────────

const LEVEL_LABELS = { rapide: 'Rapide', standard: 'Standard', approfondi: 'Approfondi' };

/**
 * Lecture tolérante de `effort` reçu du serveur ; null si absent ou inexploitable.
 * `action` (interface seulement) : action rapide qui a fixé le niveau (« Point de situation »).
 * @returns {{niveau: string|null, source: 'auto'|'utilisateur'|null, effort: string|null,
 *   reflexion: boolean|null, raison: string|null, degrade: boolean, action?: string}|null}
 */
export function normalizeEffortInfo(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const niveau = typeof data.niveau === 'string' && data.niveau.trim() ? data.niveau.trim() : null;
  const degrade = data.degrade === true;
  if (!niveau && data.effort == null && !degrade) return null;
  const info = {
    niveau,
    source: data.source === 'auto' || data.source === 'utilisateur' ? data.source : null,
    effort: typeof data.effort === 'string' ? data.effort : null,
    reflexion: typeof data.reflexion === 'boolean' ? data.reflexion : null,
    raison: typeof data.raison === 'string' && data.raison.trim() ? data.raison.trim().slice(0, 300) : null,
    degrade,
  };
  if (typeof data.action === 'string' && data.action.trim()) info.action = data.action.trim().slice(0, 80);
  return info;
}

/** « Approfondi (auto) », « Rapide » ; '' si le niveau est inconnu ou non appliqué (degrade). */
export function effortLabel(info) {
  if (!info?.niveau || info.degrade) return '';
  const label = LEVEL_LABELS[info.niveau] ?? info.niveau;
  return info.source === 'auto' ? `${label} (auto)` : label;
}

// Modèle sans niveau d'effort : aucun niveau affiché, comme le compositeur (« Profondeur fixe »)
export const EFFORT_FIXED = 'Profondeur fixe';
export const EFFORT_DEGRADED = 'réflexion non disponible pour ce modèle';

/** Partie « effort » du résumé : « Approfondi (auto) », « Profondeur fixe · réflexion non disponible… ». */
export function effortSummary(info) {
  if (info?.degrade) return `${EFFORT_FIXED} · ${EFFORT_DEGRADED}`;
  return effortLabel(info);
}

/**
 * Info-bulle : raison du choix automatique, action qui a fixé le niveau, mention d'un
 * modèle sans réflexion (seule, sans « Choisi automatiquement » : aucun niveau appliqué).
 */
export function effortTooltip(info) {
  if (!info) return '';
  if (info.degrade) return effortSummary(info);
  if (info.source === 'auto') return info.raison ? `Choisi automatiquement : ${info.raison}` : 'Choisi automatiquement selon la question';
  if (info.source === 'utilisateur') return info.action ? `Niveau fixé par l'action ${info.action}` : 'Niveau choisi dans le compositeur';
  return '';
}
